import { Card, CardContent } from "@/components/ui/card";
import { Wallet, FileText, TrendingUp } from "lucide-react";
import type { DashboardSummary } from "@/lib/calculationEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";

interface Props {
  summary: DashboardSummary;
  lumpSumSummary?: DashboardSummary | null;
  jurisdiction: Jurisdiction;
  isInterestOnly?: boolean;
}

/** Three grouped metric sections: Current Loan · Commitment · Family Financing Margin™. */
export function GroupedMetrics({ summary, lumpSumSummary, jurisdiction, isInterestOnly }: Props) {
  const { fmt } = createFormatter(jurisdiction);
  const original = summary.standardLoanPaymentCount;
  const current = lumpSumSummary?.policyLoanPaymentCount ?? summary.policyLoanPaymentCount;
  const monthsSaved = Math.max(0, original - current);
  const projectedMargin = lumpSumSummary?.additionalIntendedFinancingMargin ?? summary.additionalIntendedFinancingMargin;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Group title="Current Loan" icon={<Wallet className="h-4 w-4 text-[hsl(var(--status-active))]" />}>
        <Row label="Total Interest (Policy)" value={fmt(summary.policyLoanTotalInterest)} />
        <Row label="Payments Remaining (est.)" value={`${current}`} />
        <Row label={isInterestOnly ? "Interest-Only Payment" : "Target Payment"} value={fmt(summary.targetPaymentPerPeriod)} />
      </Group>

      <Group title="Commitment" icon={<FileText className="h-4 w-4 text-[hsl(var(--status-info))]" />}>
        <Row label="Committed Schedule" value={`${original} payments`} />
        <Row label="Current Strategy" value={`${current} payments`} />
        <Row label="Payments Saved" value={`${monthsSaved}`} accent={monthsSaved > 0} />
      </Group>

      <Group title="Family Financing Margin™" icon={<TrendingUp className="h-4 w-4 text-[hsl(var(--status-success))]" />}>
        <Row label="Current" value={fmt(summary.additionalIntendedFinancingMargin)} />
        <Row label="Projected (w/ Lump Sums)" value={fmt(projectedMargin)} accent={projectedMargin > summary.additionalIntendedFinancingMargin} />
        <Row label="Potential Redirect" value={fmt(projectedMargin)} />
      </Group>
    </div>
  );
}

function Group({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 mb-3">
          {icon}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <div className="space-y-2">{children}</div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${accent ? "text-[hsl(var(--status-success))]" : ""}`}>{value}</span>
    </div>
  );
}